import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { marked } from "marked";
import TurndownService from "turndown";

const turndown = new TurndownService({
  headingStyle: "atx",
  hr: "---",
  bulletListMarker: "-",
  codeBlockStyle: "fenced"
});

export default function registerMarkdownEditor(Alpine) {
  Alpine.data("markdownRichText", function (initialMarkdown, onChangeFn) {
    return {
      editor: null,
      htmlMode: false,
      rawMarkdown: initialMarkdown || "",

      init() {
        this.$nextTick(() => this.createEditor());
      },

      createEditor() {
        const el = this.$refs.editorArea;
        if (!el) return;

        const initialHtml = this.rawMarkdown ? marked.parse(this.rawMarkdown) : "";

        this.editor = new Editor({
          element: el,
          extensions: [
            StarterKit.configure({ heading: { levels: [2, 3] } }),
            Underline,
            Link.configure({ openOnClick: false })
          ],
          content: initialHtml,
          editorProps: {
            attributes: {
              class: "tiptap-content prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-[120px] p-3"
            }
          },
          onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            this.rawMarkdown = turndown.turndown(html);
            if (typeof onChangeFn === "function") onChangeFn(this.rawMarkdown);
          }
        });
      },

      toggleMode() {
        if (this.htmlMode) {
          // Markdown → Rich Text: parse markdown to HTML, push into editor
          if (this.editor) {
            const html = this.rawMarkdown ? marked.parse(this.rawMarkdown) : "";
            this.editor.commands.setContent(html);
          }
        } else {
          // Rich Text → Markdown: convert editor HTML to markdown
          if (this.editor) {
            this.rawMarkdown = turndown.turndown(this.editor.getHTML());
          }
        }
        this.htmlMode = !this.htmlMode;
      },

      onRawInput(value) {
        this.rawMarkdown = value;
        if (typeof onChangeFn === "function") onChangeFn(value);
      },

      // ── Toolbar actions ────────────────────────────────────
      bold() {
        this.editor?.chain().focus().toggleBold().run();
      },
      italic() {
        this.editor?.chain().focus().toggleItalic().run();
      },
      underline() {
        this.editor?.chain().focus().toggleUnderline().run();
      },
      heading(level) {
        this.editor?.chain().focus().toggleHeading({ level }).run();
      },
      bulletList() {
        this.editor?.chain().focus().toggleBulletList().run();
      },
      orderedList() {
        this.editor?.chain().focus().toggleOrderedList().run();
      },
      blockquote() {
        this.editor?.chain().focus().toggleBlockquote().run();
      },
      link() {
        const url = prompt("URL:");
        if (url) this.editor?.chain().focus().setLink({ href: url }).run();
      },
      hr() {
        this.editor?.chain().focus().setHorizontalRule().run();
      },
      editorUndo() {
        this.editor?.chain().focus().undo().run();
      },
      editorRedo() {
        this.editor?.chain().focus().redo().run();
      },

      // ── Active state getters ───────────────────────────────
      get isBold() {
        return this.editor?.isActive("bold") || false;
      },
      get isItalic() {
        return this.editor?.isActive("italic") || false;
      },
      get isUnderline() {
        return this.editor?.isActive("underline") || false;
      },
      get isH2() {
        return this.editor?.isActive("heading", { level: 2 }) || false;
      },
      get isH3() {
        return this.editor?.isActive("heading", { level: 3 }) || false;
      },
      get isBulletList() {
        return this.editor?.isActive("bulletList") || false;
      },
      get isOrderedList() {
        return this.editor?.isActive("orderedList") || false;
      },
      get isBlockquote() {
        return this.editor?.isActive("blockquote") || false;
      },

      destroy() {
        this.editor?.destroy();
        this.editor = null;
      }
    };
  });
}
